import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, BookOpen, Beaker, Atom, Dna, Calculator,
  Sparkles, Loader2, ChevronRight, ChevronDown, Lightbulb,
  FileText, HelpCircle, Zap, BookMarked, Brain, Layers,
  CheckCircle, XCircle, RefreshCw, Bot
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SubjectThumbnail from '../components/SubjectThumbnail';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// LLM Models available (randomly selected per request)
const LLM_MODELS = [
  { id: 'openai', name: 'GPT-4o', description: 'Fast & balanced', icon: '🤖' },
  { id: 'anthropic', name: 'Claude Sonnet', description: 'Detailed explanations', icon: '🧠' },
  { id: 'gemini', name: 'Gemini Flash', description: 'Quick responses', icon: '⚡' }
];

// Helper to get random LLM
const getRandomLLM = () => LLM_MODELS[Math.floor(Math.random() * LLM_MODELS.length)].id;

// Subject icons mapping
const SUBJECT_ICONS = {
  maths: Calculator,
  physics: Atom,
  chemistry: Beaker,
  biology: Dna
};

const SUBJECT_COLORS = {
  maths: { bg: 'from-blue-600 to-indigo-700', border: 'border-blue-500/30', text: 'text-blue-600', badge: 'bg-blue-500/20' },
  physics: { bg: 'from-amber-500 to-orange-600', border: 'border-amber-500/30', text: 'text-amber-600', badge: 'bg-amber-500/20' },
  chemistry: { bg: 'from-emerald-500 to-teal-600', border: 'border-emerald-500/30', text: 'text-emerald-600', badge: 'bg-emerald-500/20' },
  biology: { bg: 'from-pink-500 to-rose-600', border: 'border-pink-500/30', text: 'text-pink-600', badge: 'bg-pink-500/20' }
};

const STEMLibrary = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('maths');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedYearGroup, setSelectedYearGroup] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [customTopic, setCustomTopic] = useState('');
  const [generatedContent, setGeneratedContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contentType, setContentType] = useState('description');
  const [practiceQuestions, setPracticeQuestions] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [llmUsed, setLlmUsed] = useState(null);

  // Fetch topics on mount
  useEffect(() => {
    fetchTopics();
  }, []);

  // Reset category when subject changes
  useEffect(() => {
    setSelectedCategory(null);
    setSelectedYearGroup(null);
    setSelectedTopic(null);
    setGeneratedContent(null);
    setPracticeQuestions(null);
  }, [selectedSubject]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/api/stem-library/topics`);
      setTopics(response.data);
    } catch (error) {
      toast.error('Failed to load topics');
    }
  };

  const generateContent = async (type) => {
    const topicToUse = selectedTopic || customTopic;
    if (!topicToUse) {
      toast.error('Please select or enter a topic');
      return;
    }

    setLoading(true);
    setContentType(type);
    
    try {
      const randomLLM = getRandomLLM();
      const response = await axios.post(`${API}/api/stem-library/generate`, {
        subject: selectedSubject,
        topic: topicToUse,
        content_type: type,
        llm_provider: randomLLM
      });
      
      // Store which LLM was used
      setLlmUsed(response.data.llm_used);
      
      if (type === 'questions') {
        try {
          const questions = JSON.parse(response.data.content);
          setPracticeQuestions(questions);
          setCurrentQuestionIndex(0);
          setSelectedAnswer(null);
          setShowExplanation(false);
          setScore({ correct: 0, total: 0 });
        } catch {
          setGeneratedContent(response.data.content);
        }
      } else {
        setGeneratedContent(response.data.content);
        setPracticeQuestions(null);
      }
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} generated!`);
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const checkAnswer = () => {
    if (selectedAnswer === null) return;
    setShowExplanation(true);
    const isCorrect = selectedAnswer === practiceQuestions[currentQuestionIndex].correct_answer;
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < practiceQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const SubjectIcon = SUBJECT_ICONS[selectedSubject] || Calculator;
  const colors = SUBJECT_COLORS[selectedSubject];
  const subjectData = topics?.[selectedSubject];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100/50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(-1)}
                className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl bg-gradient-to-br ${colors.bg}`}>
                  <Layers className="h-5 w-5 text-slate-900" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">STEM Library</h1>
                  <p className="text-xs text-slate-500">KS3 & GCSE Learning</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`${colors.badge} ${colors.text} border-0`}>
                Year 9-10 / GCSE
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Subject Tabs */}
      <div className="sticky top-[73px] z-40 bg-white backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 py-3 overflow-x-auto scrollbar-hide">
            {topics && Object.entries(topics).map(([key, subject]) => {
              const Icon = SUBJECT_ICONS[key];
              const isActive = selectedSubject === key;
              const subjectColors = SUBJECT_COLORS[key];
              
              return (
                <Button
                  key={key}
                  variant={isActive ? "default" : "ghost"}
                  className={`flex-shrink-0 gap-2 transition-all ${
                    isActive 
                      ? `bg-gradient-to-r ${subjectColors.bg} text-white shadow-lg` 
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                  onClick={() => setSelectedSubject(key)}
                  data-testid={`subject-tab-${key}`}
                >
                  <Icon className="h-4 w-4" />
                  {subject.name}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Topics Sidebar */}
          <div className="lg:col-span-3">
            <Card className="bg-white border-slate-200 sticky top-[150px]">
              <CardHeader className="pb-3">
                <CardTitle className="text-slate-900 text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Topics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
                {/* Year-group structured topics (preferred when present) */}
                {subjectData?.year_groups && Object.entries(subjectData.year_groups).map(([yearGroup, categoryMap]) => (
                  <div key={yearGroup} className="space-y-1">
                    <button
                      className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-all ${
                        selectedYearGroup === yearGroup
                          ? `${colors.badge} ${colors.text}`
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                      onClick={() => {
                        setSelectedYearGroup(selectedYearGroup === yearGroup ? null : yearGroup);
                        setSelectedCategory(null);
                      }}
                      data-testid={`year-group-${yearGroup.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <span className="font-semibold text-sm">{yearGroup}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${selectedYearGroup === yearGroup ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {selectedYearGroup === yearGroup && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-2 space-y-1 py-1">
                            {Object.entries(categoryMap).map(([category, topicList]) => {
                              const categoryKey = `${yearGroup}::${category}`;
                              const isCatActive = selectedCategory === categoryKey;
                              return (
                                <div key={categoryKey} className="space-y-1">
                                  <button
                                    className={`w-full flex items-center justify-between p-2 rounded-md text-left transition-all ${
                                      isCatActive
                                        ? `${colors.badge} ${colors.text}`
                                        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                                    }`}
                                    onClick={() => setSelectedCategory(isCatActive ? null : categoryKey)}
                                  >
                                    <span className="text-xs font-medium">{category}</span>
                                    <ChevronRight className={`h-3 w-3 transition-transform ${isCatActive ? 'rotate-90' : ''}`} />
                                  </button>
                                  <AnimatePresence>
                                    {isCatActive && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                      >
                                        <div className="pl-3 space-y-1 py-1">
                                          {topicList.map((topic) => (
                                            <button
                                              key={topic}
                                              className={`w-full text-left p-2 rounded-md text-xs transition-all ${
                                                selectedTopic === topic
                                                  ? `${colors.badge} ${colors.text} font-medium`
                                                  : 'text-slate-500 hover:text-slate-600 hover:bg-slate-100'
                                              }`}
                                              onClick={() => {
                                                setSelectedTopic(topic);
                                                setCustomTopic('');
                                                setGeneratedContent(null);
                                                setPracticeQuestions(null);
                                              }}
                                            >
                                              {topic}
                                            </button>
                                          ))}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}

                {/* Legacy flat categories (only render when year_groups not provided) */}
                {!subjectData?.year_groups && subjectData?.categories && Object.entries(subjectData.categories).map(([category, topicList]) => (
                  <div key={category} className="space-y-1">
                    <button
                      className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-all ${
                        selectedCategory === category 
                          ? `${colors.badge} ${colors.text}` 
                          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                      onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                    >
                      <span className="font-medium text-sm">{category}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${selectedCategory === category ? 'rotate-180' : ''}`} />
                    </button>
                    
                    <AnimatePresence>
                      {selectedCategory === category && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-3 space-y-1 py-1">
                            {topicList.map((topic) => (
                              <button
                                key={topic}
                                className={`w-full text-left p-2 rounded-md text-xs transition-all ${
                                  selectedTopic === topic 
                                    ? `${colors.badge} ${colors.text} font-medium` 
                                    : 'text-slate-500 hover:text-slate-600 hover:bg-slate-100'
                                }`}
                                onClick={() => {
                                  setSelectedTopic(topic);
                                  setCustomTopic('');
                                  setGeneratedContent(null);
                                  setPracticeQuestions(null);
                                }}
                              >
                                {topic}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}

                {/* Custom Topic Input */}
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-2">Or enter custom topic:</p>
                  <Input
                    placeholder="Type any topic..."
                    value={customTopic}
                    onChange={(e) => {
                      setCustomTopic(e.target.value);
                      setSelectedTopic(null);
                    }}
                    className="bg-slate-100 border-slate-200 text-slate-900 text-sm placeholder:text-slate-600"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-9 space-y-6">
            {/* Selected Topic Header */}
            {(selectedTopic || customTopic) && (
              <Card className={`bg-gradient-to-r ${colors.bg} border-0`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge className="bg-slate-100 text-slate-900 mb-2">{subjectData?.name}</Badge>
                      <h2 className="text-2xl font-bold text-slate-900 mb-1">
                        {selectedTopic || customTopic}
                      </h2>
                      <p className="text-slate-600 text-sm">
                        {selectedCategory ? (selectedCategory.includes('::') ? selectedCategory.split('::')[1] : selectedCategory) : 'Custom Topic'}
                      </p>
                    </div>
                    <SubjectThumbnail subject={selectedSubject} size="lg" animate={false} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            {(selectedTopic || customTopic) && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Button
                  variant="outline"
                  className={`flex-col h-auto py-4 ${colors.border} bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900`}
                  onClick={() => generateContent('description')}
                  disabled={loading}
                  data-testid="generate-description"
                >
                  <FileText className="h-5 w-5 mb-1" />
                  <span className="text-xs">Description</span>
                </Button>
                <Button
                  variant="outline"
                  className={`flex-col h-auto py-4 ${colors.border} bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900`}
                  onClick={() => generateContent('examples')}
                  disabled={loading}
                  data-testid="generate-examples"
                >
                  <Lightbulb className="h-5 w-5 mb-1" />
                  <span className="text-xs">Examples</span>
                </Button>
                <Button
                  variant="outline"
                  className={`flex-col h-auto py-4 ${colors.border} bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900`}
                  onClick={() => generateContent('questions')}
                  disabled={loading}
                  data-testid="generate-questions"
                >
                  <HelpCircle className="h-5 w-5 mb-1" />
                  <span className="text-xs">Questions</span>
                </Button>
                <Button
                  variant="outline"
                  className={`flex-col h-auto py-4 ${colors.border} bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900`}
                  onClick={() => generateContent('simplify')}
                  disabled={loading}
                  data-testid="generate-simplify"
                >
                  <Zap className="h-5 w-5 mb-1" />
                  <span className="text-xs">Simplify</span>
                </Button>
                <Button
                  variant="outline"
                  className={`flex-col h-auto py-4 ${colors.border} bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900`}
                  onClick={() => generateContent('summarize')}
                  disabled={loading}
                  data-testid="generate-summarize"
                >
                  <BookMarked className="h-5 w-5 mb-1" />
                  <span className="text-xs">Summarize</span>
                </Button>
                <Button
                  variant="outline"
                  className={`flex-col h-auto py-4 ${colors.border} bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900`}
                  onClick={() => generateContent('elaborate')}
                  disabled={loading}
                  data-testid="generate-elaborate"
                >
                  <Brain className="h-5 w-5 mb-1" />
                  <span className="text-xs">Elaborate</span>
                </Button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-12 text-center">
                  <Loader2 className={`h-12 w-12 ${colors.text} animate-spin mx-auto mb-4`} />
                  <p className="text-slate-500">Generating {contentType}...</p>
                  <p className="text-slate-600 text-sm mt-1">This may take a few seconds</p>
                </CardContent>
              </Card>
            )}

            {/* Practice Questions */}
            {practiceQuestions && !loading && (
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <HelpCircle className={`h-5 w-5 ${colors.text}`} />
                      Practice Questions
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {llmUsed && (
                        <Badge variant="outline" className="border-cyan-500/50 text-cyan-600 text-xs">
                          {llmUsed}
                        </Badge>
                      )}
                      <Badge className={`${colors.badge} ${colors.text}`}>
                        {currentQuestionIndex + 1} / {practiceQuestions.length}
                      </Badge>
                    </div>
                  </div>
                  {score.total > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="border-green-500/50 text-green-600">
                        Score: {score.correct}/{score.total}
                      </Badge>
                      <Badge variant="outline" className={`${score.correct/score.total >= 0.7 ? 'border-green-500/50 text-green-600' : 'border-amber-500/50 text-amber-600'}`}>
                        {Math.round((score.correct/score.total) * 100)}%
                      </Badge>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Question */}
                  <div className={`p-4 rounded-xl ${colors.badge}`}>
                    <Badge className="mb-2 bg-slate-50 text-slate-600 text-xs">
                      {practiceQuestions[currentQuestionIndex].difficulty}
                    </Badge>
                    <p className="text-slate-900 text-lg font-medium">
                      {practiceQuestions[currentQuestionIndex].question}
                    </p>
                  </div>

                  {/* Options */}
                  <div className="space-y-2">
                    {practiceQuestions[currentQuestionIndex].options.map((option, index) => {
                      const isSelected = selectedAnswer === index;
                      const isCorrect = index === practiceQuestions[currentQuestionIndex].correct_answer;
                      const showResult = showExplanation;
                      
                      let optionClass = 'border-slate-200 text-slate-600 hover:border-slate-200 hover:bg-slate-100';
                      if (showResult) {
                        if (isCorrect) {
                          optionClass = 'border-green-500 bg-green-500/20 text-green-600';
                        } else if (isSelected && !isCorrect) {
                          optionClass = 'border-red-500 bg-red-500/20 text-red-600';
                        }
                      } else if (isSelected) {
                        optionClass = `${colors.border} ${colors.badge} ${colors.text}`;
                      }
                      
                      return (
                        <button
                          key={index}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${optionClass}`}
                          onClick={() => handleAnswerSelect(index)}
                          disabled={showExplanation}
                        >
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            showResult && isCorrect ? 'bg-green-500 text-white' :
                            showResult && isSelected && !isCorrect ? 'bg-red-500 text-white' :
                            isSelected ? `bg-gradient-to-r ${colors.bg} text-white` :
                            'bg-slate-100 text-slate-500'
                          }`}>
                            {String.fromCharCode(65 + index)}
                          </span>
                          <span className="flex-1">{option}</span>
                          {showResult && isCorrect && <CheckCircle className="h-5 w-5 text-green-600" />}
                          {showResult && isSelected && !isCorrect && <XCircle className="h-5 w-5 text-red-600" />}
                        </button>
                      );
                    })}
                  </div>

                  {/* Explanation */}
                  {showExplanation && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-slate-100 border border-slate-200"
                    >
                      <p className="text-sm text-slate-500 mb-1">Explanation:</p>
                      <p className="text-slate-900">{practiceQuestions[currentQuestionIndex].explanation}</p>
                    </motion.div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    {!showExplanation ? (
                      <Button
                        className={`flex-1 bg-gradient-to-r ${colors.bg}`}
                        onClick={checkAnswer}
                        disabled={selectedAnswer === null}
                      >
                        Check Answer
                      </Button>
                    ) : currentQuestionIndex < practiceQuestions.length - 1 ? (
                      <Button
                        className={`flex-1 bg-gradient-to-r ${colors.bg}`}
                        onClick={nextQuestion}
                      >
                        Next Question
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
                        onClick={() => generateContent('questions')}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Generate More Questions
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Generated Content */}
            {generatedContent && !loading && !practiceQuestions && (
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      {contentType === 'description' && <FileText className={`h-5 w-5 ${colors.text}`} />}
                      {contentType === 'examples' && <Lightbulb className={`h-5 w-5 ${colors.text}`} />}
                      {contentType === 'simplify' && <Zap className={`h-5 w-5 ${colors.text}`} />}
                      {contentType === 'summarize' && <BookMarked className={`h-5 w-5 ${colors.text}`} />}
                      {contentType === 'elaborate' && <Brain className={`h-5 w-5 ${colors.text}`} />}
                      {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
                    </CardTitle>
                    {llmUsed && (
                      <Badge variant="outline" className="border-cyan-500/50 text-cyan-600 text-xs">
                        Generated by {llmUsed}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-invert prose-slate max-w-none">
                    <div className="text-slate-600 whitespace-pre-wrap leading-relaxed">
                      {generatedContent}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {!selectedTopic && !customTopic && !loading && (
              <Card className="bg-white border-slate-200 border-dashed">
                <CardContent className="p-12 text-center">
                  <SubjectThumbnail subject={selectedSubject} size="lg" animate={false} className="mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Select a {subjectData?.name} Topic
                  </h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    Choose a topic from the sidebar or enter a custom topic to generate 
                    AI-powered learning content, examples, and practice questions.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default STEMLibrary;
